import {useEffect, useState} from 'react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {DialogBody} from '@/components/shared/DialogBody'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {Switch} from '@/components/ui/switch'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {EnumSelectItems} from '@/components/shared/EnumSelectItems'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormSection} from '@/components/shared/FormSection'
import {SmallText} from '@/components/shared/SmallText'
import {Stack} from '@/components/shared/Stack'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {ResponseFiltersEditor} from '@/components/inbound-connectors/ResponseFiltersEditor'
import {InboundAuthType, PaginationStyleBadge, ResponseFormat, SIGNING_ALGORITHM_OPTIONS} from '@/types/inbound-connector'
import type {RestInboundConfig, ScimInboundConfig} from '@/types/inbound-connector'
import {useUpdateRestConfig, useUpdateScimConfig} from '@/hooks/use-inbound-connectors'

function buildFullUrl(baseUrl: string, endpoint: string, queryParams: string | null | undefined): string {
    let base = baseUrl ?? ''
    const path = endpoint ?? ''
    if (base.endsWith('/') && path.startsWith('/')) base = base.slice(0, -1)
    else if (!base.endsWith('/') && path && !path.startsWith('/')) base = base + '/'
    const url = base + path
    const q = (queryParams ?? '').trim()
    return q ? `${url}?${q}` : url
}

function itemsPathHelpText(format: ResponseFormat, i18n: ReturnType<typeof useI18n>): string {
    if (format === ResponseFormat.XML) return i18n.inboundConnectors.itemsPathHelpXml as string
    if (format === ResponseFormat.CSV) return i18n.inboundConnectors.itemsPathHelpCsv as string
    if (format === ResponseFormat.TEXT) return i18n.inboundConnectors.itemsPathHelpText as string
    return i18n.inboundConnectors.itemsPathHelpJson as string
}

function itemsPathPlaceholder(format: ResponseFormat, i18n: ReturnType<typeof useI18n>): string {
    if (format === ResponseFormat.XML || format === ResponseFormat.JSON) {
        return itemsPathHelpText(format, i18n)
    }
    return ''
}

interface RestConfigEditDialogProps {
    connectorId: string
    config: RestInboundConfig
    open: boolean
    onClose: () => void
}

export function RestConfigEditDialog({connectorId, config, open, onClose}: RestConfigEditDialogProps) {
    const i18n = useI18n()
    const updateMutation = useUpdateRestConfig(connectorId)

    const [baseUrl, setBaseUrl] = useState(config.baseUrl)
    const [authType, setAuthType] = useState(config.authType)
    const [tokenSecretRef, setTokenSecretRef] = useState('')
    const [tokenUrl, setTokenUrl] = useState(config.tokenUrl ?? '')
    const [clientId, setClientId] = useState(config.clientId ?? '')
    const [clientSecretRef, setClientSecretRef] = useState('')
    const [scope, setScope] = useState(config.scope ?? '')
    const [username, setUsername] = useState(config.username ?? '')
    const [passwordSecretRef, setPasswordSecretRef] = useState('')
    const [apiKeyHeader, setApiKeyHeader] = useState(config.apiKeyHeader ?? '')
    const [apiKeySecretRef, setApiKeySecretRef] = useState('')
    const [clientCertSecretRef, setClientCertSecretRef] = useState('')
    const [clientKeySecretRef, setClientKeySecretRef] = useState('')
    const [truststoreSecretRef, setTruststoreSecretRef] = useState('')
    const [awsAccessKeySecretRef, setAwsAccessKeySecretRef] = useState('')
    const [awsSecretKeySecretRef, setAwsSecretKeySecretRef] = useState('')
    const [awsRegion, setAwsRegion] = useState(config.awsRegion ?? '')
    const [awsService, setAwsService] = useState(config.awsService ?? '')
    const [signingAlgorithm, setSigningAlgorithm] = useState(config.signingAlgorithm ?? '')
    const [signingKeySecretRef, setSigningKeySecretRef] = useState('')
    const [signingHeaderTemplate, setSigningHeaderTemplate] = useState(config.signingHeaderTemplate ?? '')
    const [kerberosPrincipal, setKerberosPrincipal] = useState(config.kerberosPrincipal ?? '')
    const [kerberosKeytabSecretRef, setKerberosKeytabSecretRef] = useState('')
    const [kerberosRealm, setKerberosRealm] = useState(config.kerberosRealm ?? '')
    const [googleDelegatedUser, setGoogleDelegatedUser] = useState(config.googleDelegatedUser ?? '')
    const [googleSaEmail, setGoogleSaEmail] = useState(config.googleSaEmail ?? '')
    const [googleSaKeySecretRef, setGoogleSaKeySecretRef] = useState(config.googleSaKeySecretRef ?? '')
    const [googleTokenScope, setGoogleTokenScope] = useState(config.googleTokenScope ?? '')
    const [itemsEndpoint, setItemsEndpoint] = useState(config.itemsEndpoint)
    const [itemsJsonPath, setItemsJsonPath] = useState(config.itemsJsonPath)
    const [responseFormat, setResponseFormat] = useState<ResponseFormat>(config.responseFormat)
    const [totalCountJsonPath, setTotalCountJsonPath] = useState(config.totalCountJsonPath ?? '')
    const [paginationStyle, setPaginationStyle] = useState(config.paginationStyle)
    const [pageSize, setPageSize] = useState(String(config.pageSize))
    const [connectTimeoutMs, setConnectTimeoutMs] = useState(String(config.connectTimeoutMs))
    const [readTimeoutMs, setReadTimeoutMs] = useState(String(config.readTimeoutMs))
    const [maxRetries, setMaxRetries] = useState(String(config.maxRetries))
    const [queryFilter, setQueryFilter] = useState(config.queryFilter ?? '')
    const [responseFilters, setResponseFilters] = useState<{field: string; value: string}[]>(
        (config.responseFilters ?? []).map(f => ({field: f.fieldName, value: f.expectedValue})),
    )

    const signingHeaderTemplateError = (signingHeaderTemplate.includes('\n') || signingHeaderTemplate.includes('\r'))
        ? (i18n.inboundConnectors.signingHeaderTemplateNewlineError as string)
        : undefined

    const isGoogleSaJwt = authType === InboundAuthType.GOOGLE_SA_JWT
    const containsNewline = (s: string) => s.includes('\n') || s.includes('\r')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const SECRET_REF_PREFIXES = ['vault:', 'env:', 'plain:'] as const
    const hasSecretRefPrefix = (s: string) => SECRET_REF_PREFIXES.some(p => s.startsWith(p))

    const googleDelegatedUserError = (() => {
        if (!isGoogleSaJwt) return undefined
        if (!googleDelegatedUser.trim()) return i18n.inboundConnectors.googleFieldRequiredError as string
        if (containsNewline(googleDelegatedUser)) return i18n.inboundConnectors.googleNewlineError as string
        if (!emailRegex.test(googleDelegatedUser)) return i18n.inboundConnectors.googleEmailFormatError as string
        return undefined
    })()
    const googleSaEmailError = (() => {
        if (!isGoogleSaJwt) return undefined
        if (!googleSaEmail.trim()) return i18n.inboundConnectors.googleFieldRequiredError as string
        if (containsNewline(googleSaEmail)) return i18n.inboundConnectors.googleNewlineError as string
        if (!emailRegex.test(googleSaEmail)) return i18n.inboundConnectors.googleEmailFormatError as string
        return undefined
    })()
    const googleSaKeySecretRefError = (() => {
        if (!isGoogleSaJwt) return undefined
        if (!googleSaKeySecretRef.trim()) return i18n.inboundConnectors.googleFieldRequiredError as string
        if (!hasSecretRefPrefix(googleSaKeySecretRef)) return i18n.inboundConnectors.googleSaKeySecretRefPrefixError as string
        return undefined
    })()
    const googleTokenScopeError = (() => {
        if (!isGoogleSaJwt) return undefined
        if (!googleTokenScope.trim()) return i18n.inboundConnectors.googleFieldRequiredError as string
        if (containsNewline(googleTokenScope)) return i18n.inboundConnectors.googleNewlineError as string
        return undefined
    })()
    const hasGoogleErrors = isGoogleSaJwt && (
        googleDelegatedUserError !== undefined
        || googleSaEmailError !== undefined
        || googleSaKeySecretRefError !== undefined
        || googleTokenScopeError !== undefined
    )

    useEffect(() => {
        if (open) {
            setBaseUrl(config.baseUrl)
            setAuthType(config.authType)
            setTokenSecretRef('')
            setTokenUrl(config.tokenUrl ?? '')
            setClientId(config.clientId ?? '')
            setClientSecretRef('')
            setScope(config.scope ?? '')
            setUsername(config.username ?? '')
            setPasswordSecretRef('')
            setApiKeyHeader(config.apiKeyHeader ?? '')
            setApiKeySecretRef('')
            setClientCertSecretRef('')
            setClientKeySecretRef('')
            setTruststoreSecretRef('')
            setAwsAccessKeySecretRef('')
            setAwsSecretKeySecretRef('')
            setAwsRegion(config.awsRegion ?? '')
            setAwsService(config.awsService ?? '')
            setSigningAlgorithm(config.signingAlgorithm ?? '')
            setSigningKeySecretRef('')
            setSigningHeaderTemplate(config.signingHeaderTemplate ?? '')
            setKerberosPrincipal(config.kerberosPrincipal ?? '')
            setKerberosKeytabSecretRef('')
            setKerberosRealm(config.kerberosRealm ?? '')
            setGoogleDelegatedUser(config.googleDelegatedUser ?? '')
            setGoogleSaEmail(config.googleSaEmail ?? '')
            setGoogleSaKeySecretRef(config.googleSaKeySecretRef ?? '')
            setGoogleTokenScope(config.googleTokenScope ?? '')
            setItemsEndpoint(config.itemsEndpoint)
            setItemsJsonPath(config.itemsJsonPath)
            setResponseFormat(config.responseFormat)
            setTotalCountJsonPath(config.totalCountJsonPath ?? '')
            setPaginationStyle(config.paginationStyle)
            setPageSize(String(config.pageSize))
            setConnectTimeoutMs(String(config.connectTimeoutMs))
            setReadTimeoutMs(String(config.readTimeoutMs))
            setMaxRetries(String(config.maxRetries))
            setQueryFilter(config.queryFilter ?? '')
            setResponseFilters((config.responseFilters ?? []).map(f => ({field: f.fieldName, value: f.expectedValue})))
        }
    }, [open, config])

    const handleSave = () => {
        updateMutation.mutate({
            baseUrl,
            authType,
            tokenSecretRef: tokenSecretRef || null,
            tokenUrl: tokenUrl || null,
            clientId: clientId || null,
            clientSecretRef: clientSecretRef || null,
            scope: scope || null,
            username: username || null,
            passwordSecretRef: passwordSecretRef || null,
            apiKeyHeader: apiKeyHeader || null,
            apiKeySecretRef: apiKeySecretRef || null,
            clientCertSecretRef: clientCertSecretRef || null,
            clientKeySecretRef: clientKeySecretRef || null,
            truststoreSecretRef: truststoreSecretRef || null,
            awsAccessKeySecretRef: awsAccessKeySecretRef || null,
            awsSecretKeySecretRef: awsSecretKeySecretRef || null,
            awsRegion: awsRegion || null,
            awsService: awsService || null,
            signingAlgorithm: signingAlgorithm || null,
            signingKeySecretRef: signingKeySecretRef || null,
            signingHeaderTemplate: signingHeaderTemplate || null,
            kerberosPrincipal: kerberosPrincipal || null,
            kerberosKeytabSecretRef: kerberosKeytabSecretRef || null,
            kerberosRealm: kerberosRealm || null,
            googleDelegatedUser: isGoogleSaJwt ? (googleDelegatedUser || null) : null,
            googleSaEmail: isGoogleSaJwt ? (googleSaEmail || null) : null,
            googleSaKeySecretRef: isGoogleSaJwt ? (googleSaKeySecretRef || null) : null,
            googleTokenScope: isGoogleSaJwt ? (googleTokenScope || null) : null,
            itemsEndpoint,
            // WHY: spec §3.4 — clear the items path on save when format is CSV/TEXT so
            // the backend round-trips the unused-state cleanly.
            itemsJsonPath: (responseFormat === ResponseFormat.CSV || responseFormat === ResponseFormat.TEXT)
                ? null
                : itemsJsonPath,
            responseFormat,
            totalCountJsonPath: totalCountJsonPath || null,
            paginationStyle,
            pageSize: parseInt(pageSize, 10) || 1,
            connectTimeoutMs: parseInt(connectTimeoutMs, 10) || 5000,
            readTimeoutMs: parseInt(readTimeoutMs, 10) || 10000,
            maxRetries: parseInt(maxRetries, 10) || 0,
            queryFilter: queryFilter || null,
            responseFilters: responseFilters
                .filter(f => f.field.trim() !== '')
                // eslint-disable-next-line i18next/no-literal-string -- 'EQUALS' is the API enum value for ResponseFilterOperator, not a UI label
                .map(f => ({fieldName: f.field.trim(), operator: 'EQUALS', expectedValue: f.value})),
            version: config.version,
        }, {
            onSuccess: onClose,
        })
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{i18n.inboundConnectors.editRestConfig}</DialogTitle>
                    <DialogDescription>{i18n.inboundConnectors.editRestConfigDescription}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <FormSection title={i18n.inboundConnectors.sectionConnection}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.baseUrl} htmlFor="rest-baseUrl" required>
                                <Input id="rest-baseUrl" value={baseUrl} onChange={e => setBaseUrl(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.itemsEndpoint} htmlFor="rest-itemsEndpoint" required>
                                <Input id="rest-itemsEndpoint" value={itemsEndpoint} onChange={e => setItemsEndpoint(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.queryParams} htmlFor="rest-queryFilter">
                                <Input
                                    id="rest-queryFilter"
                                    value={queryFilter}
                                    onChange={e => setQueryFilter(e.target.value)}
                                    placeholder={i18n.inboundConnectors.queryParamsPlaceholder}
                                />
                            </FormField>
                            <FormField label={i18n.inboundConnectors.fullUrl} htmlFor="rest-fullUrl">
                                <Input id="rest-fullUrl" value={buildFullUrl(baseUrl, itemsEndpoint, queryFilter)} readOnly className="font-mono text-xs"/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.itemsJsonPath} htmlFor="rest-itemsJsonPath" required={responseFormat === ResponseFormat.JSON || responseFormat === ResponseFormat.XML}>
                                <Input
                                    id="rest-itemsJsonPath"
                                    data-testid="input-items-json-path"
                                    value={itemsJsonPath}
                                    onChange={e => setItemsJsonPath(e.target.value)}
                                    disabled={responseFormat === ResponseFormat.CSV || responseFormat === ResponseFormat.TEXT}
                                    placeholder={itemsPathPlaceholder(responseFormat, i18n)}
                                />
                                <SmallText className="mt-1 text-muted-foreground">{itemsPathHelpText(responseFormat, i18n)}</SmallText>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.responseFormat} htmlFor="rest-responseFormat" required>
                                <Select value={responseFormat} onValueChange={(v) => setResponseFormat(v as ResponseFormat)}>
                                    <SelectTrigger id="rest-responseFormat" data-testid="select-response-format">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ResponseFormat.JSON} data-testid="response-format-json">{i18n.inboundConnectors.responseFormatJson}</SelectItem>
                                        <SelectItem value={ResponseFormat.XML} data-testid="response-format-xml">{i18n.inboundConnectors.responseFormatXml}</SelectItem>
                                        <SelectItem value={ResponseFormat.CSV} data-testid="response-format-csv">{i18n.inboundConnectors.responseFormatCsv}</SelectItem>
                                        <SelectItem value={ResponseFormat.TEXT} data-testid="response-format-text">{i18n.inboundConnectors.responseFormatText}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.totalCountJsonPath} htmlFor="rest-totalCountJsonPath">
                                <Input id="rest-totalCountJsonPath" value={totalCountJsonPath} onChange={e => setTotalCountJsonPath(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>
                    <FormSection title={i18n.inboundConnectors.responseFiltersSection}>
                        <ResponseFiltersEditor rows={responseFilters} onChange={setResponseFilters}/>
                    </FormSection>
                    <FormSection title={i18n.inboundConnectors.sectionAuthentication}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.authType} htmlFor="rest-authType" required>
                                <Select value={authType} onValueChange={setAuthType}>
                                    <SelectTrigger id="rest-authType" data-testid="select-rest-auth-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={InboundAuthType.NONE}>{i18n.inboundConnectors.authNone}</SelectItem>
                                        <SelectItem value={InboundAuthType.BEARER_TOKEN}>{i18n.inboundConnectors.authBearer}</SelectItem>
                                        <SelectItem value={InboundAuthType.OAUTH2_CLIENT}>{i18n.inboundConnectors.authOAuth2}</SelectItem>
                                        <SelectItem value={InboundAuthType.BASIC_AUTH}>{i18n.inboundConnectors.authBasic}</SelectItem>
                                        <SelectItem value={InboundAuthType.API_KEY}>{i18n.inboundConnectors.authApiKey}</SelectItem>
                                        <SelectItem value={InboundAuthType.MUTUAL_TLS} data-testid="auth-type-option-mutual-tls">{i18n.inboundConnectors.authMutualTls}</SelectItem>
                                        <SelectItem value={InboundAuthType.AWS_SIGV4} data-testid="auth-type-option-aws-sigv4">{i18n.inboundConnectors.authAwsSigV4}</SelectItem>
                                        <SelectItem value={InboundAuthType.SIGNED_REQUEST} data-testid="auth-type-option-signed-request">{i18n.inboundConnectors.authSignedRequest}</SelectItem>
                                        <SelectItem value={InboundAuthType.NTLM_KERBEROS} data-testid="auth-type-option-ntlm-kerberos">{i18n.inboundConnectors.authNtlmKerberos}</SelectItem>
                                        <SelectItem value={InboundAuthType.GOOGLE_SA_JWT} data-testid="auth-type-option-google-sa-jwt">{i18n.inboundConnectors.authGoogleSaJwt}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            {authType === InboundAuthType.BEARER_TOKEN && (
                                <FormField label={i18n.inboundConnectors.tokenRef} htmlFor="rest-tokenSecretRef">
                                    <SecretReferenceInput
                                        id="rest-tokenSecretRef"
                                        value={tokenSecretRef}
                                        onChange={setTokenSecretRef}
                                        placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                        data-testid="rest-token-secret-ref"
                                    />
                                </FormField>
                            )}
                            {authType === InboundAuthType.OAUTH2_CLIENT && (<>
                                <FormField label={i18n.inboundConnectors.tokenUrl} htmlFor="rest-tokenUrl">
                                    <Input id="rest-tokenUrl" value={tokenUrl} onChange={e => setTokenUrl(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.clientId} htmlFor="rest-clientId">
                                    <Input id="rest-clientId" value={clientId} onChange={e => setClientId(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.clientSecretRef} htmlFor="rest-clientSecretRef">
                                    <SecretReferenceInput
                                        id="rest-clientSecretRef"
                                        value={clientSecretRef}
                                        onChange={setClientSecretRef}
                                        placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                        data-testid="rest-client-secret-ref"
                                    />
                                </FormField>
                                <FormField label={i18n.inboundConnectors.oauthScope} htmlFor="rest-scope">
                                    <Input id="rest-scope" value={scope} onChange={e => setScope(e.target.value)}/>
                                </FormField>
                            </>)}
                            {authType === InboundAuthType.BASIC_AUTH && (<>
                                <FormField label={i18n.inboundConnectors.username} htmlFor="rest-username">
                                    <Input id="rest-username" value={username} onChange={e => setUsername(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.passwordRef} htmlFor="rest-passwordSecretRef">
                                    <SecretReferenceInput
                                        id="rest-passwordSecretRef"
                                        value={passwordSecretRef}
                                        onChange={setPasswordSecretRef}
                                        placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                        data-testid="rest-password-secret-ref"
                                    />
                                </FormField>
                            </>)}
                            {authType === InboundAuthType.API_KEY && (<>
                                <FormField label={i18n.inboundConnectors.apiKeyHeader} htmlFor="rest-apiKeyHeader">
                                    <Input id="rest-apiKeyHeader" value={apiKeyHeader} onChange={e => setApiKeyHeader(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.apiKeyRef} htmlFor="rest-apiKeySecretRef">
                                    <SecretReferenceInput
                                        id="rest-apiKeySecretRef"
                                        value={apiKeySecretRef}
                                        onChange={setApiKeySecretRef}
                                        placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                        data-testid="rest-api-key-secret-ref"
                                    />
                                </FormField>
                            </>)}
                            {authType === InboundAuthType.MUTUAL_TLS && (<>
                                <SmallText className="text-muted-foreground" data-testid="mtls-section-help">
                                    {i18n.inboundConnectors.mtlsSectionHelp}
                                </SmallText>
                                <FormField label={i18n.inboundConnectors.clientCertSecretRef} htmlFor="rest-clientCertSecretRef">
                                    <SecretReferenceInput
                                        id="rest-clientCertSecretRef"
                                        data-testid="input-client-cert-secret-ref"
                                        value={clientCertSecretRef}
                                        onChange={setClientCertSecretRef}
                                        placeholder={i18n.inboundConnectors.clientCertSecretRefPlaceholder}
                                    />
                                </FormField>
                                <FormField label={i18n.inboundConnectors.clientKeySecretRef} htmlFor="rest-clientKeySecretRef">
                                    <SecretReferenceInput
                                        id="rest-clientKeySecretRef"
                                        data-testid="input-client-key-secret-ref"
                                        value={clientKeySecretRef}
                                        onChange={setClientKeySecretRef}
                                        placeholder={i18n.inboundConnectors.clientKeySecretRefPlaceholder}
                                    />
                                </FormField>
                                <FormField label={i18n.inboundConnectors.truststoreSecretRef} htmlFor="rest-truststoreSecretRef">
                                    <SecretReferenceInput
                                        id="rest-truststoreSecretRef"
                                        data-testid="input-truststore-secret-ref"
                                        value={truststoreSecretRef}
                                        onChange={setTruststoreSecretRef}
                                        placeholder={i18n.inboundConnectors.secretRefPlaceholder}
                                    />
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.truststoreSecretRefHelp}
                                    </SmallText>
                                </FormField>
                            </>)}
                            {authType === InboundAuthType.AWS_SIGV4 && (<>
                                <SmallText className="text-muted-foreground" data-testid="sigv4-section-help">
                                    {i18n.inboundConnectors.sigV4SectionHelp}
                                </SmallText>
                                <FormField label={i18n.inboundConnectors.awsAccessKeySecretRef} htmlFor="rest-awsAccessKeySecretRef">
                                    <SecretReferenceInput
                                        id="rest-awsAccessKeySecretRef"
                                        data-testid="input-aws-access-key-secret-ref"
                                        value={awsAccessKeySecretRef}
                                        onChange={setAwsAccessKeySecretRef}
                                        placeholder={i18n.inboundConnectors.awsAccessKeySecretRefPlaceholder}
                                    />
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.awsAccessKeySecretRefHelp}
                                    </SmallText>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.awsSecretKeySecretRef} htmlFor="rest-awsSecretKeySecretRef">
                                    <SecretReferenceInput
                                        id="rest-awsSecretKeySecretRef"
                                        data-testid="input-aws-secret-key-secret-ref"
                                        value={awsSecretKeySecretRef}
                                        onChange={setAwsSecretKeySecretRef}
                                        placeholder={i18n.inboundConnectors.awsSecretKeySecretRefPlaceholder}
                                    />
                                </FormField>
                                <FormField label={i18n.inboundConnectors.awsRegion} htmlFor="rest-awsRegion">
                                    <Input id="rest-awsRegion" data-testid="input-aws-region"
                                           value={awsRegion}
                                           onChange={e => setAwsRegion(e.target.value)}
                                           placeholder={i18n.inboundConnectors.awsRegionPlaceholder}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.awsService} htmlFor="rest-awsService">
                                    <Input id="rest-awsService" data-testid="input-aws-service"
                                           value={awsService}
                                           onChange={e => setAwsService(e.target.value)}
                                           placeholder={i18n.inboundConnectors.awsServicePlaceholder}/>
                                </FormField>
                            </>)}
                            {authType === InboundAuthType.SIGNED_REQUEST && (<>
                                <SmallText className="text-muted-foreground" data-testid="signed-request-section-help">
                                    {i18n.inboundConnectors.signedRequestSectionHelp}
                                </SmallText>
                                <FormField label={i18n.inboundConnectors.signingAlgorithm} htmlFor="rest-signingAlgorithm" required>
                                    <Select value={signingAlgorithm} onValueChange={setSigningAlgorithm}>
                                        <SelectTrigger id="rest-signingAlgorithm" data-testid="select-signing-algorithm">
                                            <SelectValue/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SIGNING_ALGORITHM_OPTIONS.map(alg => (
                                                <SelectItem key={alg} value={alg} data-testid={`signing-algorithm-option-${alg.toLowerCase()}`}>{alg}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.signingKeySecretRef} htmlFor="rest-signingKeySecretRef" required>
                                    <SecretReferenceInput
                                        id="rest-signingKeySecretRef"
                                        data-testid="input-signing-key-secret-ref"
                                        value={signingKeySecretRef}
                                        onChange={setSigningKeySecretRef}
                                        placeholder={i18n.inboundConnectors.signingKeySecretRefPlaceholder}
                                    />
                                </FormField>
                                <FormField label={i18n.inboundConnectors.signingHeaderTemplate} htmlFor="rest-signingHeaderTemplate"
                                           required error={signingHeaderTemplateError}>
                                    <Input id="rest-signingHeaderTemplate" data-testid="input-signing-header-template"
                                           value={signingHeaderTemplate}
                                           onChange={e => setSigningHeaderTemplate(e.target.value)}
                                           /* eslint-disable-next-line i18next/no-literal-string -- placeholder echoes the literal template syntax operators must use */
                                           placeholder={i18n.inboundConnectors.signingHeaderTemplatePlaceholder({signature: '{{signature}}'})}/>
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {/* eslint-disable-next-line i18next/no-literal-string -- help text echoes the literal template syntax operators must use */}
                                        {i18n.inboundConnectors.signingHeaderTemplateHelp({timestamp: '{{timestamp}}', nonce: '{{nonce}}', signature: '{{signature}}'})}
                                    </SmallText>
                                </FormField>
                            </>)}
                            {authType === InboundAuthType.NTLM_KERBEROS && (<>
                                <SmallText className="text-muted-foreground" data-testid="kerberos-section-help">
                                    {i18n.inboundConnectors.kerberosSectionHelp}
                                </SmallText>
                                <FormField label={i18n.inboundConnectors.kerberosPrincipal} htmlFor="rest-kerberosPrincipal" required>
                                    <Input id="rest-kerberosPrincipal" data-testid="input-kerberos-principal"
                                           value={kerberosPrincipal}
                                           onChange={e => setKerberosPrincipal(e.target.value)}
                                           placeholder={i18n.inboundConnectors.kerberosPrincipalPlaceholder}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.kerberosKeytabSecretRef} htmlFor="rest-kerberosKeytabSecretRef" required>
                                    <SecretReferenceInput
                                        id="rest-kerberosKeytabSecretRef"
                                        data-testid="input-kerberos-keytab-secret-ref"
                                        value={kerberosKeytabSecretRef}
                                        onChange={setKerberosKeytabSecretRef}
                                        placeholder={i18n.inboundConnectors.kerberosKeytabSecretRefPlaceholder}
                                    />
                                </FormField>
                                <FormField label={i18n.inboundConnectors.kerberosRealm} htmlFor="rest-kerberosRealm">
                                    <Input id="rest-kerberosRealm" data-testid="input-kerberos-realm"
                                           value={kerberosRealm}
                                           onChange={e => setKerberosRealm(e.target.value)}/>
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.kerberosRealmHelp}
                                    </SmallText>
                                </FormField>
                            </>)}
                            {isGoogleSaJwt && (<>
                                <SmallText className="text-muted-foreground" data-testid="google-sa-section-help">
                                    {i18n.inboundConnectors.googleSaSectionHelp}
                                </SmallText>
                                <FormField label={i18n.inboundConnectors.googleDelegatedUser} htmlFor="rest-googleDelegatedUser" required error={googleDelegatedUserError}>
                                    <Input id="rest-googleDelegatedUser" data-testid="input-google-delegated-user"
                                           type="email"
                                           value={googleDelegatedUser}
                                           onChange={e => setGoogleDelegatedUser(e.target.value)}
                                           placeholder={i18n.inboundConnectors.googleDelegatedUserPlaceholder}/>
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.googleDelegatedUserHelp}
                                    </SmallText>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.googleSaEmail} htmlFor="rest-googleSaEmail" required error={googleSaEmailError}>
                                    <Input id="rest-googleSaEmail" data-testid="input-google-sa-email"
                                           type="email"
                                           value={googleSaEmail}
                                           onChange={e => setGoogleSaEmail(e.target.value)}
                                           placeholder={i18n.inboundConnectors.googleSaEmailPlaceholder}/>
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.googleSaEmailHelp}
                                    </SmallText>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.googleSaKeySecretRef} htmlFor="rest-googleSaKeySecretRef" required error={googleSaKeySecretRefError}>
                                    <SecretReferenceInput
                                        id="rest-googleSaKeySecretRef"
                                        data-testid="input-google-sa-key-secret-ref"
                                        value={googleSaKeySecretRef}
                                        onChange={setGoogleSaKeySecretRef}
                                        placeholder={i18n.inboundConnectors.googleSaKeySecretRefPlaceholder}
                                    />
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.googleSaKeySecretRefHelp}
                                    </SmallText>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.googleTokenScope} htmlFor="rest-googleTokenScope" required error={googleTokenScopeError}>
                                    <Input id="rest-googleTokenScope" data-testid="input-google-token-scope"
                                           value={googleTokenScope}
                                           onChange={e => setGoogleTokenScope(e.target.value)}
                                           placeholder={i18n.inboundConnectors.googleTokenScopePlaceholder}/>
                                    <SmallText className="mt-1 text-muted-foreground">
                                        {i18n.inboundConnectors.googleTokenScopeHelp}
                                    </SmallText>
                                </FormField>
                            </>)}
                            {authType !== config.authType && (
                                <InfoBanner variant="warning" data-testid="auth-type-change-warning">
                                    {i18n.inboundConnectors.authTypeChangeWarning}
                                </InfoBanner>
                            )}
                        </FormGrid>
                    </FormSection>
                    <FormSection title={i18n.inboundConnectors.sectionPagination}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.paginationStyle} htmlFor="rest-paginationStyle" required>
                                <Select value={paginationStyle} onValueChange={setPaginationStyle}>
                                    <SelectTrigger id="rest-paginationStyle" data-testid="select-pagination-style">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <EnumSelectItems enumClass={PaginationStyleBadge}/>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.pageSize} htmlFor="rest-pageSize" required>
                                <Input id="rest-pageSize" type="number" min={1} value={pageSize}
                                       onChange={e => setPageSize(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>
                    <FormSection title={i18n.inboundConnectors.sectionTimeouts}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.connectTimeout} htmlFor="rest-connectTimeoutMs" required>
                                <Input id="rest-connectTimeoutMs" type="number" min={1} value={connectTimeoutMs}
                                       onChange={e => setConnectTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.readTimeout} htmlFor="rest-readTimeoutMs" required>
                                <Input id="rest-readTimeoutMs" type="number" min={1} value={readTimeoutMs}
                                       onChange={e => setReadTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.maxRetries} htmlFor="rest-maxRetries" required>
                                <Input id="rest-maxRetries" type="number" min={0} value={maxRetries}
                                       onChange={e => setMaxRetries(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>
                </DialogBody>
                <DialogFooter>
                    <Button data-testid="btn-cancel-rest-config" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                        {i18n.common.cancel}
                    </Button>
                    <Button data-testid="btn-save-rest-config" onClick={handleSave}
                            disabled={updateMutation.isPending || !baseUrl || !itemsEndpoint
                                || ((responseFormat === ResponseFormat.JSON || responseFormat === ResponseFormat.XML) && !itemsJsonPath)
                                || signingHeaderTemplateError !== undefined
                                || hasGoogleErrors}>
                        {updateMutation.isPending ? <ButtonSpinner/> : null}
                        {i18n.common.saveChanges}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

interface ScimConfigEditDialogProps {
    connectorId: string
    config: ScimInboundConfig
    open: boolean
    onClose: () => void
}

export function ScimConfigEditDialog({connectorId, config, open, onClose}: ScimConfigEditDialogProps) {
    const i18n = useI18n()
    const updateMutation = useUpdateScimConfig(connectorId)

    const [scimFilter, setScimFilter] = useState(config.scimFilter ?? '')
    const [enterpriseExtEnabled, setEnterpriseExtEnabled] = useState(config.enterpriseExtEnabled)

    useEffect(() => {
        if (open) {
            setScimFilter(config.scimFilter ?? '')
            setEnterpriseExtEnabled(config.enterpriseExtEnabled)
        }
    }, [open, config])

    const handleSave = () => {
        updateMutation.mutate({
            scimFilter: scimFilter || null,
            enterpriseExtEnabled,
            version: config.version,
        }, {
            onSuccess: onClose,
        })
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{i18n.inboundConnectors.editScimConfig}</DialogTitle>
                    <DialogDescription>{i18n.inboundConnectors.editScimConfigDescription}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <FormGrid columns={1}>
                        <FormField label={i18n.inboundConnectors.scimFilter} htmlFor="scim-filter">
                            <Input id="scim-filter" value={scimFilter} onChange={e => setScimFilter(e.target.value)}/>
                        </FormField>
                        <Stack gap="sm" className="flex-row items-center">
                            <Switch
                                id="scim-enterprise-ext"
                                checked={enterpriseExtEnabled}
                                onCheckedChange={setEnterpriseExtEnabled}
                            />
                            <Label htmlFor="scim-enterprise-ext">{i18n.inboundConnectors.enterpriseExtension}</Label>
                        </Stack>
                    </FormGrid>
                </DialogBody>
                <DialogFooter>
                    <Button data-testid="btn-cancel-scim-config" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                        {i18n.common.cancel}
                    </Button>
                    <Button data-testid="btn-save-scim-config" onClick={handleSave} disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? <ButtonSpinner/> : null}
                        {i18n.common.saveChanges}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export {buildFullUrl}
